import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Trophy, 
  Plus, 
  Search, 
  Calendar, 
  Users, 
  DollarSign,
  MapPin,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];
const STATUSES = ['All', 'upcoming', 'active', 'completed'];

export const Leagues = () => {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ageFilter, setAgeFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchLeagues = async () => {
    try {
      const response = await axios.get(`${API}/leagues`);
      setLeagues(response.data);
    } catch (error) {
      console.error('Failed to fetch leagues:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLeagues = leagues.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(search.toLowerCase());
    const matchesAge = ageFilter === 'All' || l.age_category === ageFilter;
    const matchesGender = genderFilter === 'All' || l.gender === genderFilter;
    const matchesStatus = statusFilter === 'All' || l.status === statusFilter;
    return matchesSearch && matchesAge && matchesGender && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">LEAGUES</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Join seasonal leagues with weekly schedules
          </p>
        </div>
        
        {(user?.role === 'admin' || user?.role === 'organizer') && (
          <Link to="/leagues/create" className="mt-4 md:mt-0">
            <Button className="btn-primary gap-2">
              <Plus className="w-4 h-4" />
              Create League
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-8">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search leagues..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-full md:w-[140px]">
                <SelectValue placeholder="Age" />
              </SelectTrigger>
              <SelectContent>
                {AGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[140px]">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s === 'All' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leagues Grid */}
      {filteredLeagues.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLeagues.map((league) => (
            <Card 
              key={league.id} 
              className="card-hover overflow-hidden"
            >
              <div className="h-2 bg-gradient-to-r from-primary to-accent" />
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <Badge variant="outline" className={getStatusColor(league.status)}>
                    {league.status.toUpperCase()}
                  </Badge>
                  <Badge variant="secondary">
                    {league.age_category} • {league.gender}
                  </Badge>
                </div>
                <CardTitle className="text-xl font-bold mt-2">{league.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {league.description}
                </p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>{league.weeks} Weeks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span>{league.registered_teams}/{league.max_teams} Teams</span>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs">
                      ${league.entry_fee} entry + $10 registration = ${(league.entry_fee + 10).toFixed(2)} total
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="truncate">{league.location || 'TBA'}</span>
                  </div>
                </div>
                
                <Link to={`/leagues/${league.id}`}>
                  <Button className="w-full gap-2" variant="outline">
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Leagues Found</h3>
            <p className="text-muted-foreground mb-6">
              {search || ageFilter !== 'All' || genderFilter !== 'All' || statusFilter !== 'All'
                ? 'Try adjusting your filters'
                : 'Be the first to create a league'}
            </p>
            {(user?.role === 'admin' || user?.role === 'organizer') && (
              <Link to="/leagues/create">
                <Button className="btn-primary gap-2">
                  <Plus className="w-4 h-4" />
                  Create League
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
